import React from 'react';
import { List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { NavLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import styled from 'styled-components';

type NavItemProps = {
  to: string;
  icon: React.ReactNode;
  label: string;
  // Rendered but inert: for a link that has nothing sensible to point at right now.
  disabled?: boolean;
  onNavigate?: () => void;
};

const NavItem: React.FC<NavItemProps> = ({ to, icon, label, disabled, onNavigate }) => (
  <NavItemButton
    component={NavLink}
    to={to}
    onClick={onNavigate}
    disabled={disabled}
    aria-disabled={disabled}
    disableRipple
  >
    <ListItemIcon>{icon}</ListItemIcon>
    <ListItemText primary={label} />
  </NavItemButton>
);

/** The column of nav items both drawer tabs use. */
export const NavList = styled(List).attrs({ disablePadding: true })<{ $offset?: number }>`
  margin-top: ${({ $offset = 32 }) => $offset}px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const NavItemButton = styled(ListItemButton)<{ component: typeof NavLink; to: string }>`
  gap: 12px;
  color: ${({ theme }) => theme.palette.common.white};
  /* The negative margin cancels the padding, so the pill grows around the item without moving it. */
  padding: 8px 12px;
  margin: -8px -12px;
  min-height: 0;
  border-radius: 8px;
  transition: background-color ${({ theme }) => theme.transitions.duration.shortest}ms;

  &:hover {
    background-color: ${({ theme }) => alpha(theme.palette.common.white, 0.07)};
  }

  &.Mui-focusVisible {
    background-color: transparent;
    outline: 2px solid ${({ theme }) => theme.palette.common.white};
  }

  &.active,
  &.active:hover,
  &.active.Mui-focusVisible {
    background-color: ${({ theme }) => alpha(theme.palette.common.white, 0.14)};
  }

  /* Pointer events are already off. The colour is restated: an inherited one would come from outside the
     button, i.e. the page's black text. MUI's own 0.38 would be too faint on the green. */
  &.Mui-disabled {
    color: ${({ theme }) => theme.palette.common.white};
    opacity: 0.45;
  }

  & .MuiListItemText-root {
    margin-top: 0;
    margin-bottom: 0;
  }

  & .MuiListItemText-primary {
    font-size: ${({ theme }) => theme.fontSizes.lg};
    color: inherit;
  }

  &.active .MuiListItemText-primary {
    font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  }

  & .MuiListItemIcon-root {
    color: inherit;
    min-width: 0;
  }

  & .MuiListItemIcon-root .MuiSvgIcon-root {
    font-size: 32px;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    & .MuiListItemIcon-root .MuiSvgIcon-root {
      font-size: ${({ theme }) => theme.spacing(3)};
    }

    & .MuiListItemText-primary {
      font-size: ${({ theme }) => theme.fontSizes.md};
      color: inherit;
    }
  }
`;

export default NavItem;
